import type { HotEditSelection } from '../../stores/hotEdit';

/**
 * What the slim inspector shows (hot edit plan, phase 2): presets in product words instead of px
 * values, design-system colors, and a plain name for the selection. Pure, so it is tested alone.
 */

/** A preset writes one Tailwind class and previews it with inline CSS until the file reloads. */
export type Preset = { label: string; cls: string; css: Record<string, string> };

export const SIZE_PRESETS: Preset[] = [
  { label: 'Small', cls: 'text-sm', css: { fontSize: '0.875rem', lineHeight: '1.25rem' } },
  { label: 'Body', cls: 'text-base', css: { fontSize: '1rem', lineHeight: '1.5rem' } },
  { label: 'Large', cls: 'text-lg', css: { fontSize: '1.125rem', lineHeight: '1.75rem' } },
  { label: 'Title', cls: 'text-2xl', css: { fontSize: '1.5rem', lineHeight: '2rem' } },
  { label: 'Display', cls: 'text-4xl', css: { fontSize: '2.25rem', lineHeight: '2.5rem' } },
];

export const WEIGHT_PRESETS: Preset[] = [
  { label: 'Regular', cls: 'font-normal', css: { fontWeight: '400' } },
  { label: 'Medium', cls: 'font-medium', css: { fontWeight: '500' } },
  { label: 'Bold', cls: 'font-bold', css: { fontWeight: '700' } },
];

export const ALIGN_PRESETS: Preset[] = [
  { label: 'Left', cls: 'text-left', css: { textAlign: 'left' } },
  { label: 'Center', cls: 'text-center', css: { textAlign: 'center' } },
  { label: 'Right', cls: 'text-right', css: { textAlign: 'right' } },
];

export const SPACING_PRESETS: Preset[] = [
  { label: 'None', cls: 'p-0', css: { padding: '0px' } },
  { label: 'Tight', cls: 'p-2', css: { padding: '0.5rem' } },
  { label: 'Normal', cls: 'p-4', css: { padding: '1rem' } },
  { label: 'Loose', cls: 'p-6', css: { padding: '1.5rem' } },
];

/** The preset whose class the element has, if any. */
export function activePreset(className: string, presets: readonly Preset[]): Preset | undefined {
  const classes = new Set(className.split(/\s+/).filter(Boolean));
  return presets.find((p) => classes.has(p.cls));
}

/** Design-system colors, in the order the swatches show. */
const SWATCH_TOKENS: { token: string; label: string }[] = [
  { token: 'foreground', label: 'Text' },
  { token: 'muted-foreground', label: 'Muted text' },
  { token: 'primary', label: 'Primary' },
  { token: 'secondary', label: 'Secondary' },
  { token: 'accent', label: 'Accent' },
  { token: 'muted', label: 'Muted' },
  { token: 'destructive', label: 'Destructive' },
  { token: 'card', label: 'Card' },
  { token: 'background', label: 'Background' },
];

export type Swatch = { token: string; label: string; css: string };

/** `222.2 47.4% 11.2%` (shadcn HSL parts) -> `hsl(222.2 47.4% 11.2%)`; full colors stay as they are. */
export function tokenCss(value: string): string {
  const v = value.trim();
  return /^\d/.test(v) && !v.includes('(') ? `hsl(${v})` : v;
}

/** Swatches for the prototype's own colors; only tokens it defines. */
export function designSwatches(themeTokens: Record<string, string> | undefined): Swatch[] {
  if (!themeTokens) return [];
  return SWATCH_TOKENS.filter((s) => themeTokens[s.token]).map((s) => ({
    ...s,
    css: tokenCss(themeTokens[s.token]!),
  }));
}

/** `text-[#0f766e]` for a custom color under Advanced. */
export function customColorClass(prefix: 'text' | 'bg', hex: string): string | null {
  const h = hex.trim();
  return /^#[0-9a-fA-F]{3,8}$/.test(h) ? `${prefix}-[${h}]` : null;
}

const TAG_LABELS: Record<string, string> = {
  h1: 'Heading',
  h2: 'Heading',
  h3: 'Heading',
  h4: 'Heading',
  h5: 'Heading',
  h6: 'Heading',
  p: 'Text',
  span: 'Text',
  strong: 'Text',
  em: 'Text',
  label: 'Label',
  a: 'Link',
  button: 'Button',
  img: 'Image',
  svg: 'Icon',
  ul: 'List',
  ol: 'List',
  li: 'List item',
  table: 'Table',
  tr: 'Table row',
  td: 'Table cell',
  th: 'Table header',
  input: 'Input',
  textarea: 'Text area',
  select: 'Dropdown',
  nav: 'Navigation',
  header: 'Header',
  footer: 'Footer',
  section: 'Section',
  aside: 'Sidebar',
  form: 'Form',
  div: 'Box',
};

/** A plain box reads as a card when it is drawn like one (a border or a surface colour). */
/** The little coloured shape an icon sits on: round or softly cornered, with a fill of its own. */
function looksLikeTile(className: string | undefined): boolean {
  const classes = (className ?? '').split(/\s+/);
  const rounded = classes.some((c) => c === 'rounded-full' || c.startsWith('rounded'));
  const filled = classes.some((c) => c.startsWith('bg-'));
  const small = classes.some((c) => /^[wh]-(\d|1[0-6])$/.test(c));
  return rounded && filled && small;
}

function looksLikeCard(className: string | undefined): boolean {
  const classes = (className ?? '').split(/\s+/);
  return classes.some((c) => c === 'border' || c.startsWith('border-') || c === 'bg-card' || c.startsWith('shadow'));
}

/** `MetricCard` -> `Metric card`; `KPIGrid` -> `KPI grid`. */
export function humanizeComponentName(name: string): string {
  const words = name
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .split(/[\s_-]+/)
    .filter(Boolean);
  return words
    .map((w, i) => (i === 0 ? w : /^[A-Z0-9]+$/.test(w) && w.length > 1 ? w : w.toLowerCase()))
    .join(' ');
}

/** The element in product words: "Metric card", "Alert card · 3 of 5", "Heading". */
export function elementLabel(
  sel: Pick<HotEditSelection, 'tagName' | 'breadcrumb' | 'listRow' | 'listContainer' | 'className'>,
): string {
  const tag = sel.tagName.toLowerCase();
  const owner = sel.breadcrumb?.[sel.breadcrumb.length - 1]?.label;
  const plain = TAG_LABELS[tag];
  // A list row is named after the component that draws it: "Alert card", not "List item".
  if (sel.listRow?.isRowRoot && owner) {
    return `${humanizeComponentName(owner)} · ${sel.listRow.index} of ${sel.listRow.count}`;
  }
  // A small round or square box holding one thing is the tile behind an icon, and naming it
  // after the page it happens to sit in ("Ward supplies tracker page") told the founder nothing.
  const base =
    plain && plain !== 'Box'
      ? plain
      : looksLikeCard(sel.className)
        ? 'Card'
        : looksLikeTile(sel.className)
          ? 'Tile'
          : owner && !/page$/i.test(owner)
            ? humanizeComponentName(owner)
            // A layout box inside the page component is not "the page": every wrapper in a
            // one-page prototype would answer to that name, which says nothing about this one.
            : (plain ?? 'Element');
  if (sel.listContainer) return `${base} · ${sel.listContainer.count} items`;
  const row = sel.listRow;
  return row ? `${base} · ${row.index} of ${row.count}` : base;
}

